import React from "react";
import Fade from "react-reveal/Fade";
import { TextArea } from "semantic-ui-react";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { Autocomplete, TextField } from "@mui/material";
import { Switch } from "antd";
import { es, enUS } from "date-fns/locale";
import { format, parse } from "date-fns";
import { Link } from "react-router-dom";
import { formatDisplayValue } from "../../../../../utils/formatDisplayValue";

const hasRequiredValue = (value) => {
  if (value === null || value === undefined) return false;
  if (typeof value === "string") return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  return true;
};

export const Label = ({ value, required, error }) => {
  const className = `label-euro bold${error ? " label-euro-error" : ""}`;

  return (
    <label className={className}>
      {value} {required && <RequiredSpan />}
    </label>
  );
};

export const Select = ({
  field,
  error,
  value,
  options,
  handleChange,
  t,
  disableClearable = false,
  updateMassivePo,
}) => (
  <>
    <Fade left opposite>
      <span style={field.style}>
        <Label value={t(field.label)} required={field.required} error={error} />
      </span>
    </Fade>
    <Autocomplete
      closeText={false}
      openText={false}
      disableClearable={disableClearable}
      className="input-euro"
      id={field.id}
      style={{ marginTop: "3px" }}
      size="small"
      ListboxProps={{
        sx: { fontSize: 12 },
      }}
      sx={{
        "& .MuiAutocomplete-input, & .MuiInputLabel-root": {
          fontSize: 12,
        },
      }}
      options={options}
      filterOptions={(options, event) => {
        let filteredOptions = options.filter((opt) => {
          return (
            event.inputValue === "" ||
            opt.label.toLowerCase().includes(event.inputValue.toLowerCase())
          );
        });
        return filteredOptions;
      }}
      onChange={(ev, value) => {
        let valueOnChange = null;

        if (updateMassivePo) {
          valueOnChange = value;
        } else {
          valueOnChange = value?.value ?? "";
        }

        handleChange(field.id, valueOnChange);
      }}
      value={updateMassivePo ? t(value) : value}
      renderInput={(params) => <TextField {...params} variant="standard" />}
      noOptionsText={t("po_management_select_no_options")}
    />
    <Fade left opposite>
      <label style={field.style}>{field.error}</label>
    </Fade>
  </>
);

export const RequiredSpan = () => <span style={{ color: "#ff0000" }}>*</span>;

export const Input = ({
  field,
  t,
  values,
  errors = {},
  handleChange,
  handleCheckbox,
  handleOpen,
  findCatalog,
  findInCatalog,
  lng,
  isOpen,
}) => {
  const requiredError = field.required && !hasRequiredValue(values[field.id]);
  const customError = errors[field.id];
  const error = requiredError || Boolean(customError);
  if (
    field.type === "Label" ||
    field.type === "LabelRef" ||
    field.type === "Redirect"
  ) {
    const formattedLabelValue = formatDisplayValue(
      values[field.id],
      field.format
    );

    let url = "";
    if (field.type === "Redirect" && field.link && values[field.linkId]) {
      url = field.link;
      if (field.linkId) {
        url = url.replace("{id}", values[field.linkId]);
      }
    }
    return (
      <>
        <Fade left opposite>
          <span className="label-euro bold" style={field.style}>
            <label>
              {t(field.label)} {field.required && <RequiredSpan />}
            </label>
          </span>
        </Fade>
        <div style={{ display: "flex", alignItems: "center" }}>
          <span>
            {field.type === "Redirect" ? (
              <Link className="link-euro" to={url}>
                {values[field.id]}
              </Link>
            ) : formattedLabelValue !== null &&
              formattedLabelValue !== undefined &&
              formattedLabelValue !== "" ? (
              formattedLabelValue
            ) : (
              "-"
            )}
          </span>
        </div>
      </>
    );
  } else if (field.type === "Input") {
    return (
      <>
        <Fade left opposite>
          <span style={field.style}>
            <Label
              value={t(field.label)}
              required={field.required}
              error={error}
            />
          </span>
        </Fade>
        <div style={{ display: "flex", alignItems: "center" }}>
          <TextField
            variant="standard"
            type={field.isNumber ? "number" : "string"}
            name={field.id}
            value={values[field.id] || ""}
            maxLength={field.maxLength}
            minLength={field.minLength}
            InputProps={{
              classes: {
                input: "input-euro",
              },
            }}
            onKeyPress={(event) => {
              const inputMode = field.isNumber ? "numeric" : "text";
              if (inputMode === "numeric" && !/[0-9.]/.test(event.key)) {
                event.preventDefault(); // Evita caracteres no numéricos en campos de tipo número
              }
            }}
            onChange={(event) => {
              let inputValue = event.target.value;
              const maxLength = field.maxLength; // Obtener la propiedad maxLength de field
              if (field.isNumber) {
                if (inputValue === "-1") inputValue = "0";
                inputValue = inputValue.replace(/[-e]/gi, "");
              }
              if (maxLength && inputValue.length > maxLength) {
                // Si existe maxLength y la longitud del valor supera el límite
                // Recortar el valor para que tenga la longitud máxima permitida
                const trimmedValue = inputValue.slice(0, maxLength);
                handleChange(field.id, trimmedValue);
              } else {
                // Si no se supera el límite, simplemente llama a handleChange con el valor actual
                handleChange(field.id, inputValue);
              }
            }}
            max={field.max}
            key={field.id}
          />
        </div>
      </>
    );
  } else if (field.type === "TextArea") {
    return (
      <>
        <Fade left opposite>
          <span id={field.id} style={field.style}>
            <Label
              value={t(field.label)}
              required={field.required}
              error={error}
            />
          </span>
        </Fade>
        <div style={{ display: "flex", alignItems: "center" }}>
          <TextArea
            className='custom-textarea'
            required={field.required}
            onFocus={(e) => {
              field.target.setAttribute("autocomplete", "none");
            }}
            type={field.datatype}
            placeholder={t(field.placeHolder)}
            name={field.id}
            key={field.id}
            value={values[field.id]}
            style={{ width: "100%" }}
            onChange={(event) => handleChange(field.id, event.target.value)}
          />
        </div>
      </>
    );
  } else if (field.type === "Checkbox") {
    return (
      <>
        <Fade left opposite>
          <span id={field.id} style={field.style}>
            <Label
              value={t(field.label)}
              required={field.required}
              error={error}
            />
            <Switch
              className="form-input-switch"
              checked={Number(values[field.id] || 0)}
              onChange={() => handleCheckbox(field.id)}
              disabled={field.disabled}
            />
          </span>
        </Fade>
      </>
    );
  } else if (field.type === "Select") {
    return (
      <Select
        field={field}
        error={error}
        options={findCatalog(field.catalogId)}
        value={findInCatalog(values, field.id, field.catalogId)}
        handleChange={handleChange}
        values={values}
        t={t}
      />
    );
  } else if (field.type === "Date") {
    let valueDate = values[field.id] || null;
    if (valueDate && !(valueDate instanceof Date)) {
      valueDate = format(
        parse(valueDate, "dd-MM-yyyy", new Date()),
        "MM-dd-yyyy"
      );
    }
    return (
      <>
        <Fade left opposite>
          <span style={field.style}>
            <Label
              value={t(field.label)}
              required={field.required}
              error={error}
            />
          </span>
        </Fade>
        <LocalizationProvider
          adapterLocale={lng === "US" ? enUS : es}
          dateAdapter={AdapterDateFns}
        >
          <DatePicker
            className="input-euro date-picker-po-items"
            placeholder={t(field.label)}
            OpenPickerButtonProps={{ style: { display: "none" } }}
            open={Boolean(isOpen[field.id])}
            onOpen={() => handleOpen(field.id, true)}
            onClose={() => handleOpen(field.id, false)}
            id={field.id}
            options={[]}
            inputProps={{
              placeholder: lng === "US" ? "mm/dd/yyyy" : 'dd/mm/aaaa'
            }}
            renderInput={(params) => (
              <TextField
                InputProps={{
                  classes: {
                    input: "input-euro",
                  },
                }}
                onClick={(e) => handleOpen(field.id, true)}
                variant="standard"
                {...params}
              />
            )}
            value={valueDate}
            onChange={(value) => {
              // let date = moment(value)
              // console.log('date????', date, value)
              // if (!date.isValid()) {
              //   handleChange(field.id, '');
              //   return;
              // }
              handleChange(field.id, value);
            }}
          />
        </LocalizationProvider>
        <Fade left opposite>
          <label style={field.style}>{field.error}</label>
        </Fade>
      </>
    );
  }
  return null;
};
